import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

type ResultData = {
  attemptId: string;
  testId: string;
  courseId: string;
  testTitle: string;
  totalQuestions: number;
  answered: number;
  review: number;
  warnings: number;
  correct: number;
  wrong: number;
  score: number;
  accuracy: number;
  submittedAt: string;
};

const TestResult: React.FC = () => {
  const { attemptId } = useParams();
  const [result, setResult] = useState<ResultData | null>(null);

  useEffect(() => {
    if (!attemptId) return;

    const raw = sessionStorage.getItem(`test_attempt_${attemptId}`);

    if (raw) {
      try {
        setResult(JSON.parse(raw));
        return;
      } catch {
        setResult(null);
      }
    }

    setResult({
      attemptId,
      testId: "",
      courseId: "",
      testTitle: "Test Submitted",
      totalQuestions: 8,
      answered: 7,
      review: 1,
      warnings: 0,
      correct: 0,
      wrong: 0,
      score: 0,
      accuracy: 0,
      submittedAt: new Date().toISOString(),
    });
  }, [attemptId]);

  if (!result) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f7ff] p-4 font-['Inter']">
        <div className="rounded-[28px] bg-white p-8 text-center shadow-sm">
          <h1 className="text-2xl font-black text-slate-950">Result not found</h1>
          <p className="mt-2 text-sm font-semibold text-slate-500">
            Attempt data nahi mila.
          </p>
          <button
            onClick={() => (window.location.href = "/student-dashboard/tests")}
            className="mt-6 rounded-2xl bg-[#0f3f70] px-6 py-3 text-sm font-black text-white"
          >
            Back to Tests
          </button>
        </div>
      </div>
    );
  }

  const unanswered = result.totalQuestions - result.answered;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f3f7ff] p-4 text-[#101827] font-['Inter']">
      <main className="w-full max-w-[720px] rounded-[30px] bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-4xl text-emerald-700">
          ✓
        </div>

        <h1 className="mt-6 text-3xl font-black tracking-[-0.04em] text-slate-950">
          Test Submitted
        </h1>

        <p className="mx-auto mt-3 max-w-xl text-sm font-semibold leading-6 text-slate-500">
          {result.testTitle} successfully submit ho gaya. Abhi demo result
          frontend se calculate ho raha hai. Backend connect hone ke baad final
          report database se show hogi.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <ResultBox label="Answered" value={String(result.answered)} />
          <ResultBox label="Review" value={String(result.review)} />
          <ResultBox label="Correct" value={String(result.correct)} />
          <ResultBox label="Wrong" value={String(result.wrong)} />
          <ResultBox label="Unanswered" value={String(unanswered)} />
          <ResultBox label="Warnings" value={String(result.warnings)} />
          <ResultBox label="Score" value={String(result.score)} />
          <ResultBox label="Accuracy" value={`${result.accuracy}%`} />
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            onClick={() =>
              (window.location.href = result.courseId
                ? `/student-dashboard/course/${result.courseId}?tab=tests`
                : "/student-dashboard/tests")
            }
            className="rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-black text-slate-700 hover:bg-blue-50"
          >
            Back to Course Tests
          </button>

          <button
            onClick={() => (window.location.href = "/student-dashboard")}
            className="rounded-2xl bg-[#0f3f70] px-6 py-3 text-sm font-black text-white hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </main>
    </div>
  );
};

const ResultBox = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="rounded-[22px] bg-slate-50 p-5">
      <p className="text-3xl font-black text-[#0f3f70]">{value}</p>
      <p className="mt-1 text-sm font-black text-slate-500">{label}</p>
    </div>
  );
};

export default TestResult;
